import findIndex from "lodash/findIndex";
import join from "lodash/join";
import map from "lodash/map";
import some from "lodash/some";
import split from "lodash/split";
import truncate from "lodash/truncate";
import { CSSProperties } from "react";
import { Length, Size, Unit } from "../../types/customization/box";
import {
  TemplateDateCellForcedTextDefinition,
  TemplateDateTextDefinition,
  TemplateEditableTextDefinition,
  TemplateFontDefinition,
  TemplateForcedTextDefinition,
} from "../../types/customization/template";
import {
  TextAlign,
  TextAlignHorizontal,
  TextAlignVertical,
  TextOrientation,
  UnsupportedTextAlignError,
  UnsupportedTextOrientationError,
} from "../../types/customization/text";
import { lengthDmmToMm, sizeDmmToMm } from "../../utils/customization/box";

export const DEFAULT_FONT_COLOR = "#000000";

export function calcMaxCharsPerLine(
  boxSizeMm: Size<Unit.MM>,
  fontSizeLengthMm: Length<Unit.MM>,
  textOrientation: TextOrientation,
  widthPerHeightMeanCharRatio: number
): number {
  let reference: Length<Unit.MM>;
  switch (textOrientation) {
    case TextOrientation.O000:
    case TextOrientation.O180:
      reference = boxSizeMm.width;
      break;
    case TextOrientation.O090:
    case TextOrientation.O270:
      reference = boxSizeMm.height;
      break;
    default: {
      const exhaustiveCheck: never = textOrientation;
      throw new UnsupportedTextOrientationError(exhaustiveCheck);
    }
  }

  const meanCharWidth = fontSizeLengthMm.value * widthPerHeightMeanCharRatio;

  return Math.floor(reference.value / meanCharWidth);
}

export function calcMaxLines(
  boxSizeMm: Size<Unit.MM>,
  lineHeightLengthMm: Length<Unit.MM>,
  textOrientation: TextOrientation
): number {
  let reference: Length<Unit.MM>;
  switch (textOrientation) {
    case TextOrientation.O000:
    case TextOrientation.O180:
      reference = boxSizeMm.height;
      break;
    case TextOrientation.O090:
    case TextOrientation.O270:
      reference = boxSizeMm.width;
      break;
    default: {
      const exhaustiveCheck: never = textOrientation;
      throw new UnsupportedTextOrientationError(exhaustiveCheck);
    }
  }

  return Math.max(Math.floor(reference.value / lineHeightLengthMm.value), 1);
}

export function calcTextProperties(
  elementDefinition:
    | TemplateDateCellForcedTextDefinition
    | TemplateDateTextDefinition
    | TemplateEditableTextDefinition
    | TemplateForcedTextDefinition,
  fontDefinition: TemplateFontDefinition
): {
  boxSize: Size<Unit.MM>;
  fontSizeLength: Length<Unit.MM>;
  lineHeightLength: Length<Unit.MM>;
  maxCharsPerLine: number;
  maxLines: number;
} {
  const boxSize = sizeDmmToMm(elementDefinition);
  const fontSizeLength = lengthDmmToMm(elementDefinition.fontSize);
  const lineHeightLength = lengthDmmToMm(elementDefinition.lineHeight);
  const maxLines = calcMaxLines(boxSize, lineHeightLength, elementDefinition.textOrientation);
  const maxCharsPerLine = calcMaxCharsPerLine(
    boxSize,
    fontSizeLength,
    elementDefinition.textOrientation,
    fontDefinition.widthPerHeightMeanCharRatio
  );

  return {
    boxSize,
    fontSizeLength,
    lineHeightLength,
    maxCharsPerLine,
    maxLines,
  };
}

// todo: remove if new textarea is kept and function not used anymore
export function calcNextContent(
  prevContent: string,
  prevMaxLines: number,
  prevMaxCharsPerLine: number,
  nextMaxLines: number,
  nextMaxCharsPerLine: number
): [string, boolean] {
  if (nextMaxLines >= prevMaxLines && nextMaxCharsPerLine >= prevMaxCharsPerLine) {
    return [prevContent, false];
  }

  if (contentFit(prevContent, nextMaxLines, nextMaxCharsPerLine)) {
    return [prevContent, false];
  }

  if (prevMaxLines === 1 && nextMaxLines === 1) {
    const truncatedContent = truncate(prevContent, {
      length: nextMaxCharsPerLine,
      omission: "",
      separator: " ",
    });
    const loss = truncatedContent.replace(/\s+/g, " ") !== prevContent.replace(/\s+/g, " ");

    return [truncatedContent, loss];
  }

  const prevLines = split(prevContent, "\n");
  const nextLines: string[] = [];
  let currentLine = "";

  for (const line of prevLines) {
    const words = line.split(RegExp("(\\p{Alphabetic}+)", "u"));

    const normalizedWords = [];
    for (const word of words) {
      if (word === "") {
        // Ignore
      } else if (word.length <= nextMaxCharsPerLine) {
        normalizedWords.push(word);
      } else {
        const subWords = word.split(new RegExp(`(.{${nextMaxCharsPerLine}})`));
        for (const subWord of subWords) {
          if (subWord !== "") {
            normalizedWords.push(subWord);
          }
        }
      }
    }
    normalizedWords.push(" ");

    for (const word of normalizedWords) {
      const cleanedWord = word.replace(/\s+/, " ");
      const candidateLine = `${currentLine}${cleanedWord}`;

      if (candidateLine.length <= nextMaxCharsPerLine) {
        currentLine = candidateLine;
      } else {
        if (nextMaxLines - nextLines.length === 1) {
          const [lastLine, loss] = calcNextContent(
            candidateLine,
            1,
            candidateLine.length,
            1,
            nextMaxCharsPerLine
          );
          return [join([...nextLines, lastLine.trim()], "\n"), loss];
        } else {
          nextLines.push(currentLine.trim());
          currentLine = word;
        }
      }
    }
  }

  nextLines.push(currentLine.trim());
  const nextContent = join(nextLines, "\n");

  return [nextContent, false];
}

function contentFit(text: string, maxLines: number, maxCharsPerLine: number): boolean {
  const lines = split(text, "\n");

  if (lines.length > maxLines) {
    return false;
  }

  if (some(lines, (line) => line.length > maxCharsPerLine)) {
    return false;
  }

  return true;
}

export function constrainText(
  text: string,
  maxLines: number,
  maxCharsPerLine: number,
  prevText: string
): string {
  if (!contentFit(text, maxLines, maxCharsPerLine)) {
    return rearrangeText(text, maxLines, maxCharsPerLine, prevText);
  }

  return text;
}

export function rearrangeText(
  text: string,
  maxLines: number,
  maxCharsPerLine: number,
  prevText: string
): string {
  const lines = text.split("\n");
  const chars = split(text, "");
  const charsByLines = map(lines, (line) => {
    return split(line, "");
  });
  const {
    line: currentLine,
    posByChars: cursorPosition,
    posInLine,
  } = getLastCursorPosition(lines, chars, prevText);

  if (lines.length > maxLines) {
    return prevText;
  }

  if (prevText.length === 0 && chars.length > maxCharsPerLine) {
    return organizeFullTextInLines(text, maxCharsPerLine, maxLines);
  }

  if (charsByLines[currentLine].length <= maxCharsPerLine) {
    return text;
  }

  if (lines.length < maxLines && chars[cursorPosition] === " ") {
    chars[cursorPosition] = "\n";
    return chars.join("");
  }

  if (lines.length < maxLines) {
    if (chars.length < split(prevText, "").length) {
      const nextSpace = charsByLines[currentLine].indexOf(" ", posInLine);
      if (nextSpace > -1 && nextSpace <= maxCharsPerLine) {
        charsByLines[currentLine][nextSpace] = "\n";
        lines[currentLine] = charsByLines[currentLine].join("");
        return lines.join("\n");
      }

      if (
        posInLine + 1 <= maxCharsPerLine &&
        charsByLines[currentLine].length - posInLine < maxCharsPerLine
      ) {
        charsByLines[currentLine][maxCharsPerLine - 1] += "\n";
        lines[currentLine] = charsByLines[currentLine].join("");
        return lines.join("\n");
      }

      if (
        posInLine + 1 >= maxCharsPerLine &&
        charsByLines[currentLine].length - posInLine >= maxCharsPerLine
      ) {
        return prevText;
      }
    }

    const lastSpace = chars.slice(0, cursorPosition + 1).lastIndexOf(" ");
    const firstSpace = chars.indexOf(" ");
    if (lastSpace > -1) {
      chars[lastSpace] = "\n";
    } else if (firstSpace > -1) {
      chars[firstSpace] = "\n";
    } else {
      chars[cursorPosition] = `\n${chars[cursorPosition]}`;
    }
    return chars.join("");
  }

  return prevText;
}

function getLastCursorPosition(
  lines: string[],
  textChars: string[],
  prevText: string
): { line: number; posByChars: number; posInLine: number } {
  const prevTextChars = split(prevText, "");
  const cursorIndex = findIndex(textChars, (char, index) => {
    return char !== prevTextChars[index];
  });
  let currentLine = 0;
  let posInLine = cursorIndex;
  let currentIndex = 0;
  if (lines.length === 1) {
    currentLine = 0;
  } else {
    for (let i = 0; i < lines.length; i++) {
      currentIndex += lines[i].length + 1;
      if (currentIndex >= cursorIndex) {
        currentLine = i;
        posInLine = lines[i].length - (currentIndex - cursorIndex);
        break;
      }
    }
  }
  return {
    line: currentLine,
    posByChars: cursorIndex > -1 ? cursorIndex : textChars.length,
    posInLine,
  };
}

function organizeFullTextInLines(text: string, maxCharsPerLine: number, maxLines: number): string {
  const words = text.split(/\s/);
  const newTextByLines: string[] = [""];
  for (const word of words) {
    const currentLineCount: number = newTextByLines.length;
    const currentLineForWords: number = currentLineCount - 1;
    if (newTextByLines[currentLineForWords].length + word.length < maxCharsPerLine) {
      const divider = newTextByLines[currentLineForWords].length > 0 ? " " : "";
      newTextByLines[currentLineForWords] += divider + word;
    } else if (currentLineCount < maxLines && word.length < maxCharsPerLine) {
      newTextByLines[currentLineForWords + 1] = word;
    } else if (currentLineCount < maxLines && word.length > maxCharsPerLine) {
      const characters = split(word, "");
      const wordChunks = Math.ceil(characters.length / maxCharsPerLine);
      if (newTextByLines[currentLineForWords].length === 0) {
        // remove first line if empty
        newTextByLines.pop();
      }
      for (let i = 0; i < wordChunks && newTextByLines.length < maxLines; i++) {
        newTextByLines.push(
          characters.slice(i * maxCharsPerLine, (i + 1) * maxCharsPerLine).join("")
        );
      }
    } else {
      break;
    }
  }
  return newTextByLines.join("\n");
}

export function textOrientationToCss(
  textOrientation: TextOrientation,
  size: Size<Unit.PX>
): CSSProperties {
  switch (textOrientation) {
    case TextOrientation.O000:
      return {};
    case TextOrientation.O090:
      return {
        height: size.width.value,
        left: "50%",
        marginLeft: (-1 * size.height.value) / 2,
        marginTop: (-1 * size.width.value) / 2,
        position: "absolute",
        top: "50%",
        transform: "rotate(-90deg)",
        transformOrigin: "center",
        width: size.height.value,
      };
    case TextOrientation.O180:
      return {
        transform: "rotate(180deg)",
        transformOrigin: "center",
      };
    case TextOrientation.O270:
      return {
        height: size.width.value,
        left: "50%",
        marginLeft: (-1 * size.height.value) / 2,
        marginTop: (-1 * size.width.value) / 2,
        position: "absolute",
        top: "50%",
        transform: "rotate(90deg)",
        transformOrigin: "center",
        width: size.height.value,
      };
    default: {
      const exhaustiveCheck: never = textOrientation;
      throw new UnsupportedTextOrientationError(exhaustiveCheck);
    }
  }
}

export function textOrientationToRotation(textOrientation: TextOrientation): number {
  switch (textOrientation) {
    case TextOrientation.O000:
      return 0;
    case TextOrientation.O090:
      return -90;
    case TextOrientation.O180:
      return 180;
    case TextOrientation.O270:
      return 90;
    default: {
      const exhaustiveCheck: never = textOrientation;
      throw new UnsupportedTextOrientationError(exhaustiveCheck);
    }
  }
}

export function textAlignToCss(textAlign: TextAlign): CSSProperties {
  const style: CSSProperties = {};

  switch (textAlign[0]) {
    case TextAlignVertical.TOP:
      style.alignItems = "flex-start";
      break;
    case TextAlignVertical.MIDDLE:
      style.alignItems = "center";
      break;
    case TextAlignVertical.BOTTOM:
      style.alignItems = "flex-end";
      break;
    default: {
      const exhaustiveCheck: never = textAlign[0];
      throw new UnsupportedTextAlignError(exhaustiveCheck);
    }
  }

  switch (textAlign[1]) {
    case TextAlignHorizontal.LEFT:
      style.textAlign = "left";
      break;
    case TextAlignHorizontal.CENTER:
      style.textAlign = "center";
      break;
    case TextAlignHorizontal.RIGHT:
      style.textAlign = "right";
      break;
    default: {
      const exhaustiveCheck: never = textAlign[1];
      throw new UnsupportedTextAlignError(exhaustiveCheck);
    }
  }

  return style;
}
